import React, { useEffect } from 'react'
import { View } from 'react-native'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'

import OpenTasksDateHeader from '../Header/OpenTasksDateHeader'
import { removeActiveDragTaskModeInDate, setSelectedTasks } from '../../../redux/actions'
import { AMOUNT_TASKS_INDEX, DATE_TASK_INDEX, EMPTY_SECTION_INDEX, TODAY_DATE } from '../../../utils/backends/openTasks'
import TopShowMoreButton from './TopShowMoreButton'
import MiddleShowMoreButton from './MiddleShowMoreButton'
import SelectedProjectEmptyInbox from './SelectedProjectEmptyInbox'
import TasksSections from './TasksSections'
import { checkIfSelectedProject } from '../../SettingsView/ProjectsSettings/ProjectHelper'
import AllProjectsShowMoreButtonContainer from './AllProjectsShowMoreButtonContainer'
import { AssistantScheduleRows } from './OpenTaskViewForAssistants/AssistantScheduleTimeline'
import WorkflowTaskCreator from './OpenTaskViewForAssistants/WorkflowTaskCreator'
import TaskListSkeleton from '../TaskListSkeleton'

export default function OpenTasksByDate({
    projectId,
    dateIndex,
    projectIndex,
    instanceKey,
    setProjectsHaveTasksInFirstDay,
    sortedLoggedUserProjectIds,
    pressedShowMoreMainSection,
    setPressedShowMoreMainSection,
    assistantProfileMode = false,
    assistantScheduleOccurrences = [],
    assistantScheduleContext = null,
    assistantTaskCreatorContext = null,
    projectCelebrationRunId = 0,
}) {
    const dispatch = useDispatch()
    const selectedProjectIndex = useSelector(state => state.selectedProjectIndex)
    const activeDragTaskModeInDate = useSelector(state => state.activeDragTaskModeInDate)
    const isAnonymous = useSelector(state => state.loggedUser.isAnonymous)
    const loggedUserProjectIds = useSelector(state => state.loggedUser.projectIds, shallowEqual)
    const dateFormated = useSelector(state => state.filteredOpenTasksStore[instanceKey][dateIndex][DATE_TASK_INDEX])
    const amountTasks = useSelector(state => state.filteredOpenTasksStore[instanceKey][dateIndex][AMOUNT_TASKS_INDEX])
    const emptyGoalsAmount = useSelector(
        state => state.filteredOpenTasksStore[instanceKey][dateIndex][EMPTY_SECTION_INDEX].length
    )
    const laterTasksExpanded = useSelector(state => state.laterTasksExpanded)
    const laterTasksExpandState = useSelector(state => state.laterTasksExpandState)
    const somedayTasksExpanded = useSelector(state => state.somedayTasksExpanded)
    const lastFormatedDate = useSelector(
        state =>
            state.filteredOpenTasksStore[instanceKey][state.filteredOpenTasksStore[instanceKey].length - 1][
                DATE_TASK_INDEX
            ]
    )

    const thereAreLaterOpenTasksInProject = useSelector(state => state.thereAreLaterOpenTasks[projectId])
    const thereAreLaterEmptyGoalsInProject = useSelector(state => state.thereAreLaterEmptyGoals[projectId])
    const thereAreSomedayOpenTasksInProject = useSelector(state => state.thereAreSomedayOpenTasks[projectId])
    const thereAreSomedayEmptyGoalsInProject = useSelector(state => state.thereAreSomedayEmptyGoals[projectId])
    const initialLoadingEndOpenTasks = useSelector(state =>
        state.initialLoadingEndOpenTasks[instanceKey] ? state.initialLoadingEndOpenTasks[instanceKey] : false
    )
    const initialLoadingEndObservedTasks = useSelector(state =>
        state.initialLoadingEndObservedTasks[instanceKey] ? state.initialLoadingEndObservedTasks[instanceKey] : false
    )
    const singleTaskIsLoading = useSelector(state => !!state.taskListSingleLoading?.[instanceKey])
    const initialTaskDataIsLoading = !initialLoadingEndOpenTasks || !initialLoadingEndObservedTasks
    const showFallbackSkeleton =
        !singleTaskIsLoading && initialTaskDataIsLoading && amountTasks === 0 && emptyGoalsAmount === 0

    const openTasksShowMoreData = useSelector(state => state.openTasksShowMoreData[projectId])
    const hasFutureTasks = openTasksShowMoreData?.hasFutureTasks

    const accessGranted = !isAnonymous && loggedUserProjectIds.includes(projectId)

    const dateIsToday = dateFormated === TODAY_DATE

    const inSelectedProject = checkIfSelectedProject(selectedProjectIndex)

    // Determine if we should show the "show more" button
    // For All Projects view: button appears after the last visible date based on expand state
    // For Selected Project view: button appears after today if not expanded
    const isLastVisibleDate = sortedLoggedUserProjectIds
        ? laterTasksExpandState === 0
            ? dateIsToday
            : dateFormated === lastFormatedDate
        : dateIsToday

    const shouldShowButtonInCurrentExpandState = sortedLoggedUserProjectIds
        ? laterTasksExpandState < 2 // In All Projects: show if not fully expanded (state 0 or 1)
        : !laterTasksExpanded // In Selected Project: show if not expanded

    const showTopShowMoreButton =
        isLastVisibleDate &&
        (thereAreLaterOpenTasksInProject ||
            thereAreLaterEmptyGoalsInProject ||
            thereAreSomedayOpenTasksInProject ||
            thereAreSomedayEmptyGoalsInProject) &&
        (inSelectedProject || shouldShowButtonInCurrentExpandState)

    const showMiddleContractShowMoreButton =
        dateFormated === lastFormatedDate && laterTasksExpanded && !somedayTasksExpanded

    const showMiddleExpandShowMoreButton =
        dateFormated === lastFormatedDate &&
        laterTasksExpanded &&
        !somedayTasksExpanded &&
        (thereAreSomedayOpenTasksInProject || thereAreSomedayEmptyGoalsInProject || hasFutureTasks)

    useEffect(() => {
        return () => {
            dispatch(removeActiveDragTaskModeInDate())
            dispatch(setSelectedTasks(null, true))
        }
    }, [])

    const showProjectEmptyInbox =
        !assistantProfileMode &&
        amountTasks === 0 &&
        assistantScheduleOccurrences.length === 0 &&
        emptyGoalsAmount === 0 &&
        initialLoadingEndOpenTasks &&
        initialLoadingEndObservedTasks

    /**
     * AT-2492 — the small pop the Anna "tasks done" picture makes when this project was cleared
     * today.
     *
     * The DECISION is not made here any more. It moved up to `OpenTasksByProject` together with the
     * celebration itself: the sweep it drives is on the project line, which in All Projects is
     * dropped from the board at the very moment the list empties, so nothing below that component
     * is still mounted to decide anything. All that is left here is forwarding, and one gate that
     * genuinely belongs to a date section: `dateIsToday`. This component renders once per date
     * section, so with Later expanded several of them can be empty at once, and without this a
     * single clearing would pop three pictures.
     */
    const emptyInboxPopRunId = dateIsToday ? projectCelebrationRunId : 0

    const isActiveOrganizeMode =
        activeDragTaskModeInDate &&
        activeDragTaskModeInDate.projectId === projectId &&
        activeDragTaskModeInDate.dateIndex === dateIndex

    const isFirstDate = dateIndex === 0

    const showAllProjectsShowMoreButtonContainer = sortedLoggedUserProjectIds && isFirstDate && laterTasksExpanded
    const showSingleTaskSkeleton = singleTaskIsLoading && dateFormated === lastFormatedDate

    return (
        <View>
            <OpenTasksDateHeader
                projectId={projectId}
                dateIndex={dateIndex}
                instanceKey={instanceKey}
                accessGranted={accessGranted}
                additionalTasksAmount={assistantScheduleOccurrences.length}
            />
            {!!assistantTaskCreatorContext && <WorkflowTaskCreator {...assistantTaskCreatorContext} />}

            {showFallbackSkeleton ? (
                <TaskListSkeleton />
            ) : (
                <TasksSections
                    projectId={projectId}
                    dateIndex={dateIndex}
                    projectIndex={projectIndex}
                    instanceKey={instanceKey}
                    isActiveOrganizeMode={isActiveOrganizeMode}
                    pressedShowMoreMainSection={pressedShowMoreMainSection}
                    setPressedShowMoreMainSection={setPressedShowMoreMainSection}
                    assistantProfileMode={assistantProfileMode}
                />
            )}
            {assistantScheduleOccurrences.length > 0 && (
                <AssistantScheduleRows
                    projectId={projectId}
                    occurrences={assistantScheduleOccurrences}
                    {...assistantScheduleContext}
                />
            )}
            {showProjectEmptyInbox && (
                <SelectedProjectEmptyInbox
                    projectId={projectId}
                    instanceKey={instanceKey}
                    celebrationRunId={emptyInboxPopRunId}
                />
            )}
            {showTopShowMoreButton && (
                <TopShowMoreButton
                    instanceKey={instanceKey}
                    projectIndex={projectIndex}
                    setProjectsHaveTasksInFirstDay={setProjectsHaveTasksInFirstDay}
                />
            )}
            {false && showAllProjectsShowMoreButtonContainer && (
                <AllProjectsShowMoreButtonContainer
                    projectIds={sortedLoggedUserProjectIds}
                    setProjectsHaveTasksInFirstDay={setProjectsHaveTasksInFirstDay}
                />
            )}

            {inSelectedProject && (
                <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
                    {showMiddleExpandShowMoreButton && (
                        <MiddleShowMoreButton
                            instanceKey={instanceKey}
                            projectIndex={projectIndex}
                            setProjectsHaveTasksInFirstDay={setProjectsHaveTasksInFirstDay}
                            expanded={false}
                        />
                    )}
                    {showMiddleContractShowMoreButton && (
                        <MiddleShowMoreButton
                            instanceKey={instanceKey}
                            projectIndex={projectIndex}
                            setProjectsHaveTasksInFirstDay={setProjectsHaveTasksInFirstDay}
                            expanded={true}
                        />
                    )}
                </View>
            )}
            {showSingleTaskSkeleton && <TaskListSkeleton rowCount={1} />}
        </View>
    )
}
